/**
 * Recent Donations Table Component
 *
 * Shows the most recent donation transactions
 */

import { FC } from 'react';
import { ArrowDownTrayIcon, EyeIcon } from '@heroicons/react/24/outline';
import { useDocumentDownload } from '@/hooks/useDocumentDownload';
import type { DashboardOverviewResponse } from '@/types/dashboard.types';

const statusColors = {
  completed: 'bg-green-100 text-green-800',
  pending: 'bg-yellow-100 text-yellow-800',
  failed: 'bg-red-100 text-red-800',
};

interface RecentDonationsTableProps {
  overviewData: DashboardOverviewResponse | null;
}

export const RecentDonationsTable: FC<RecentDonationsTableProps> = ({ overviewData }) => {
  const { downloadPdf, openPdfInTab, isDownloading, error } = useDocumentDownload();

  const handleDownloadReceipt = async (transactionId: number) => {
    try {
      await downloadPdf('transaction-receipt', transactionId);
    } catch (err) {
      console.error('Download receipt failed:', err);
    }
  };

  const handleViewReceipt = async (transactionId: number) => {
    try {
      await openPdfInTab('transaction-receipt', transactionId);
    } catch (err) {
      console.error('View receipt failed:', err);
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-md p-6">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">Recent Donations</h3>
          <p className="text-sm text-gray-600">Your latest donation transactions</p>
        </div>
        <button className="text-sm text-primary-600 hover:text-primary-700 font-medium">
          View All →
        </button>
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-sm text-red-600">{error.message}</p>
        </div>
      )}

      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead>
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Transaction ID
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Date
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Category
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Amount
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Payment Method
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Status
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Receipt
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {overviewData?.recent_donations.map((donation) => (
              <tr key={donation.transaction_id} className="hover:bg-gray-50">
                <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900">
                  {donation.transaction_id}
                </td>
                <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">
                  {new Date(donation.date).toLocaleDateString('en-US', {
                    month: 'short',
                    day: 'numeric',
                    year: 'numeric',
                  })}
                </td>
                <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                  {donation.category_name}
                </td>
                <td className="px-4 py-3 whitespace-nowrap text-sm font-semibold text-gray-900">
                  ${donation.amount.toFixed(2)}
                </td>
                <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">
                  {donation.payment_method || 'N/A'}
                </td>
                <td className="px-4 py-3 whitespace-nowrap">
                  <span
                    className={`px-2 py-1 text-xs font-medium rounded-full ${statusColors[donation.status as keyof typeof statusColors]}`}
                  >
                    {donation.status.charAt(0).toUpperCase() + donation.status.slice(1)}
                  </span>
                </td>
                <td className="px-4 py-3 whitespace-nowrap text-sm">
                  <div className="flex gap-2">
                    <button
                      onClick={() => handleViewReceipt(donation.transaction_id)}
                      disabled={isDownloading}
                      className="text-primary-600 hover:text-primary-700 inline-flex items-center disabled:opacity-50 disabled:cursor-not-allowed"
                      title="View receipt"
                    >
                      <EyeIcon className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => handleDownloadReceipt(donation.transaction_id)}
                      disabled={isDownloading}
                      className="text-primary-600 hover:text-primary-700 inline-flex items-center disabled:opacity-50 disabled:cursor-not-allowed"
                      title="Download receipt"
                    >
                      <ArrowDownTrayIcon className="h-4 w-4" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {overviewData?.recent_donations.length === 0 && (
        <div className="text-center py-8">
          <p className="text-gray-500">No donations found</p>
        </div>
      )}
    </div>
  );
};
